// SCR-007's statement panel: what the customer sees — prices only, no cost, profit or reason text.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { Empty, ErrorBanner, Loading } from "@nexus/ui";
import { Money } from "@nexus/ui";
import { NOT_BILLED_MESSAGE } from "../terms/model";
import { finalBadgeLabel, loadStatement, statementLineRow, type StatementState } from "./model";

export function StatementPreview({ orgId, period, customer, monthText }: { orgId: string; period: string; customer: string; monthText: string }) {
  const client = useApi();
  const [state, setState] = useState<StatementState>({ kind: "loading" });

  const reload = () => {
    if (!orgId || !period) return;
    setState({ kind: "loading" });
    void loadStatement(client, orgId, period).then(setState);
  };

  useEffect(reload, [client, orgId, period]);

  return (
    <Card
      title="Statement preview"
      help="terms.statement"
      actions={state.kind === "loaded" && <Chip tone={state.statement.final ? "grey" : "blue"}>{finalBadgeLabel(state.statement.final)}</Chip>}
    >
      {state.kind === "loading" && <Loading label="Loading statement…" />}
      {state.kind === "not-billed" && <Empty message={`${NOT_BILLED_MESSAGE} ${customer} is not billed for ${monthText}.`} />}
      {state.kind === "error" && <ErrorBanner error={state.error} onRetry={reload} />}
      {state.kind === "loaded" && (
        <>
          <p class="card-note">What {customer} sees: prices only, never cost, profit or adjustment reasons.</p>
          <div class="table-scroll">
            <table class="data-table">
              <caption class="visually-hidden">What the customer sees</caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Description</th>
                  <th scope="col" class="numeric">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.statement.lines.map((line) => {
                  const row = statementLineRow(line);
                  return (
                    <tr key={row.lineNo}>
                      <td>{row.lineNo}</td>
                      <td>{row.description}</td>
                      <td class="numeric">{row.priceDisplay ? <span title={row.priceDisplay.exact}>{row.priceDisplay.text}</span> : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={2}>
                    Total
                  </th>
                  <td class="numeric">
                    <Money micro={state.statement.totalMicroUsd} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {state.statement.creditCarriedOutMicroUsd > 0 && (
            <p class="card-note">
              Unused credit carried to the next statement: <Money micro={state.statement.creditCarriedOutMicroUsd} />.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
